import {useCallback, useEffect, useState} from 'react';
import {Route, Routes, useLocation, useNavigate} from 'react-router-dom';

import { useAuth0 } from '@auth0/auth0-react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

import Realistic from 'react-canvas-confetti/dist/presets/realistic';

import { deleteLocal, emptyLocal, forgetUser, getActions, getLocal, MSAL_SCOPES, resetProfile, sendAction, setLocal, syncProfile, updateProfileAsTester, wait } from './utils/api';

import Activity from './components/activity/Activity';
import Chat from './components/chat/Chat';
import Details from './components/details/Details';
import Feedback from './components/feedback/Feedback';
import Forget from './components/forget/Forget';
import Onboarding from './components/onboarding/Onboarding';
import Path from './components/path/Path';
import Phone from './components/phone/Phone';
import Plan from './components/plan/Plan';
import Profile from './components/profile/Profile';
import Splash from './components/splash/Splash';
import StartOver from './components/start-over/StartOver';
import Terms from './components/terms/Terms';

import './App.css';

function App({ authState }) {
  const { error: auth0Error, getAccessTokenSilently, isAuthenticated: auth0IsAuthenticated, isLoading: auth0IsLoading, loginWithRedirect, logout, user: auth0User } = useAuth0();
  const { instance, accounts, inProgress } = useMsal();
  const msalIsAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const navigate = useNavigate();

  const isAuthenticated = (authState.type === 'saml' ? msalIsAuthenticated : auth0IsAuthenticated);
  const isLoadingAuth = (authState.type === 'saml' ? (inProgress !== InteractionStatus.None) : auth0IsLoading);
  const user = (authState.type === 'saml' ? (instance.getActiveAccount() ?? accounts[0] ?? null) : auth0User);

  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSignedUp, setIsSignedUp] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [showStartOver, setShowStartOver] = useState(false);

  const confirmError = useCallback(() => {
    setError(null);
  }, []);

  const handleConfetti = useCallback(() => {
    setShowConfetti(true);

    setTimeout(() => {
      setShowConfetti(false);
    }, 3000);
  }, []);

  const handleError = useCallback(e => {
    console.error(e);
    // To re-enable the error UI: call setError(e.message) here and uncomment the error popup in the JSX below
  }, []);

  const handleFeedback = useCallback(() => {
    setShowFeedback(prevShow => !prevShow);
  }, []);

  const handleLogin = useCallback(e => {
    setShowLogin(prevShow => !prevShow);
  }, []);

  const handleLogout = useCallback(async () => {
    if (authState.type === 'saml') {
      const account = instance.getActiveAccount() || instance.getAllAccounts()[0];

      if (account) {
        await instance.clearCache({ account });
      }

      emptyLocal();

      if (authState.tenant?.slug) {
        setLocal('logout', authState.tenant.slug);
        window.location = '/';
      }
    } else {
      if (authState.tenant?.slug) {
        setLocal('logout', authState.tenant.slug);
      }

      logout({ logoutParams: { returnTo: window.location.origin } });
    }

    if (!authState.tenant?.slug) {
      await resetProfile();
    }
  }, [authState, instance, logout]);

  const handlePath = useCallback(() => {
    if (location.state?.from && location.state.from.startsWith('/activity')) {
      navigate(location.state.from);
    } else {
      navigate('/path');
    }
  }, [location, navigate]);

  const handlePhone = useCallback(e => {
    setShowPhone(prevShow => !prevShow);
  }, []);

  const handleStartOver = useCallback(() => {
    setShowStartOver(prevShow => !prevShow);
  }, []);

  const confirmConnection = useCallback(connection => {
    setLocal('connection', connection);
    setLocal('tenant', authState?.tenant);

    if (connection === 'sms') {
      setShowPhone(true);
    } else {
      if (authState.type === 'saml') {
        instance.loginRedirect({ scopes: MSAL_SCOPES, domainHint: authState?.tenant?.domainHint }).catch(handleError);
      } else {
        window.location = '/login';
      }
    }
  }, [authState, instance, handleError]);

  const confirmTos = useCallback(async isAccepted => {
    try {
      if (isAccepted) {
        const referralCode = getLocal('referralCode');
        const response = await sendAction('signup', null, referralCode ? { referralCode } : null);

        if (response !== null) {
          setIsSignedUp(true);
          deleteLocal('referralCode');
        }
      } else {
        await handleLogout();
      }
    } catch (e) {
      handleError(e);
    }
  }, [handleError, handleLogout]);

  const checkProfile = useCallback(async (email, phone) => {
    let response;

    try {
      response = await syncProfile(email, phone);

      if (response) {
        return response;
      }

      return null;
    } catch (e) {
      handleError(e);
    }
  }, [handleError]);

  useEffect(() => {
    const forgetMe = async () => {
      try {
        const response = await forgetUser();

        if (response?.status === 'user_deleted') {
          await handleLogout();
        }
      } catch (e) {
        handleError(e);
      }
    };

    if (location.pathname === '/forget-me') {
      forgetMe();
    } else if (location.pathname === '/logout') {
      handleLogout();
    } else if (location.pathname === '/reset') {
      emptyLocal();
    } else if (location.hash) {
      const hashParts = location.hash.substring(1).split('&');
      
      if (hashParts.length > 0) {
        hashParts.forEach(hashPart => {
          const valueParts = hashPart.split('=');

          if (valueParts.length === 2) {
            if (valueParts[0] === 'id_token' && valueParts[1]) {
              setLocal('login', true);
              setLocal('token', valueParts[1]);
            }
            
            window.location = '/';
          }
        });
      }
    }
  });

  useEffect(() => {
    if (authState.type !== 'saml') return;

    const allAccounts = instance.getAllAccounts();

    if (allAccounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(allAccounts[0]);
    }
  }, [authState.type, instance, accounts.length]);

  useEffect(() => {
    if (isLoadingAuth) {
      return;
    }

    const _connection = getLocal('connection');
    const _token = getLocal('token');

    const fetchAccessToken = async () => {
      let token;

      try {
        if (authState.type === 'saml') {
          const tokenResponse = await instance.acquireTokenSilent({ scopes: MSAL_SCOPES, account: user });

          token = tokenResponse.accessToken;
        } else {
          token = await getAccessTokenSilently();
        }
      } catch (e) {
        if (authState.type === 'saml') {
          const account = instance.getActiveAccount() || instance.getAllAccounts()[0];

          if (account) {
            await instance.clearCache({ account });
          }

          if (inProgress === InteractionStatus.None) {
            instance.loginRedirect({
              scopes: MSAL_SCOPES,
              domainHint: authState?.tenant?.domainHint,
            }).catch(handleError);
          }
        } else {
          const isRedirecting = getLocal('auth0_redirecting');

          if (isRedirecting) {
            deleteLocal('auth0_redirecting');
          } else {
            setLocal('auth0_redirecting', true);
            await loginWithRedirect();
          }
        }

        return;
      }
      
      deleteLocal('auth0_redirecting');
      setLocal('token', token);

      if (user) {
        let email;
        let phone;
        
        try {
          if (_connection === 'sms') {
            phone = user.name;
          } else {
            if (authState.type === 'saml') {
              email = user.username;
            } else {
              email = user.email;
            }
          }

          const response = await checkProfile(email, phone);

          if (!response) {
            return;
          }

          if (response?.status === 201) {
            if (authState.type === 'auth0') {
              await loginWithRedirect();
              return;
            }
          }

          if (getLocal('login')) {
            if (await sendAction('login') !== null) {
              deleteLocal('login');
              deleteLocal('signup');
            }
          }

          const isTester = getLocal('isTester');

          if (isTester === '0' || isTester === '1') {
            if (await updateProfileAsTester(isTester === '1') !== null) {
              deleteLocal('isTester');
            }
          }

          const actions = await getActions('signup');

          if (actions?.length) {
            setIsSignedUp(true);
            deleteLocal('referralCode');
          }
  
          setIsLoading(false);
        } catch (e) {
          handleError(e);
        }
      }

      return token;
    };

    if (!isAuthenticated && !_token) {
      if (_connection === 'email') {
        if (authState.type === 'saml') {
          if (inProgress === InteractionStatus.None) {
            instance.loginRedirect({ scopes: MSAL_SCOPES, domainHint: authState?.tenant?.domainHint }).catch(handleError);
          }
        } else {
          if (auth0Error) {
            handleError(auth0Error);
            return;
          }

          const isRedirecting = getLocal('auth0_redirecting');

          if (isRedirecting) {
            deleteLocal('auth0_redirecting');
          } else {
            setLocal('auth0_redirecting', true);
            loginWithRedirect();
          }
        }
      } else {
        setIsLoading(false);
      }

      return;
    }

    const locationParts = location.pathname.split('/');

    if (locationParts.length === 3 && locationParts[1] === 'referral' && locationParts[2]) {
      window.location = '/';
      return;
    }

    if (locationParts.length === 3 && locationParts[1] === 'tester' && (locationParts[2] === '0' || locationParts[2] === '1')) {
      setLocal('isTester', locationParts[2]);

      window.location = '/';
      return;
    }

    try {
      fetchAccessToken().catch(handleError);
    } catch (e) {
      handleError(e);
    }
  }, [auth0Error, authState, checkProfile, getAccessTokenSilently, handleError, handleLogout, inProgress, instance, isAuthenticated, isLoadingAuth, location, loginWithRedirect, user]);

  return (
    <>
      {isAuthenticated && !isLoading && !isLoadingAuth && isSignedUp ? (
        <Routes>
          <Route path="/" element={<Onboarding handleConfetti={handleConfetti} handleError={handleError} />} />
          <Route path="/activity/:activityId/:taskId?" element={<Activity handleConfetti={handleConfetti} handleError={handleError} handleFeedback={handleFeedback} handlePath={handlePath} handleStartOver={handleStartOver} />} />
          <Route path="/chat" element={<Chat handleError={handleError} handleFeedback={handleFeedback} handlePath={handlePath} handleStartOver={handleStartOver} />} />
          <Route path="/forget-me" element={<Forget handleError={handleError} />} />
          <Route path="/path" element={<Path handleError={handleError} handleFeedback={handleFeedback} handlePath={handlePath} handleStartOver={handleStartOver} />} />
          <Route path="/plan/:taskId?" element={<Plan handleConfetti={handleConfetti} handleError={handleError} handleFeedback={handleFeedback} handlePath={handlePath} handleStartOver={handleStartOver} />} />
          <Route path="/profile" element={<Profile handleFeedback={handleFeedback} handlePath={handlePath} handleStartOver={handleStartOver} />} />
          <Route path="/profile/details" element={<Details handleConfetti={handleConfetti} handleError={handleError} handleFeedback={handleFeedback} handlePath={handlePath} handleStartOver={handleStartOver} />} />
          <Route path="/terms-of-service" element={<Terms />} />
          <Route path="/:slug" element={<Onboarding handleConfetti={handleConfetti} handleError={handleError} />} />
        </Routes>
      ) : null}
      {isAuthenticated && !isLoading && !isLoadingAuth && !isSignedUp && location.pathname === '/terms-of-service' ? (
        <Terms />
      ) : null}
      {isAuthenticated && !isLoading && !isLoadingAuth && !isSignedUp && location.pathname !== '/terms-of-service' ? (
        <div id="tos-container" className="popup-overlay">
          <div className="popup">
            <p>I am 13 years of age or older and agree to the <a target="_blank" href="/terms-of-service">Terms of Service</a>.</p>
            <div className="actions">
              <div className="action action-primary" onClick={() => confirmTos(true)}>Accept</div>
              <div className="action action-secondary" onClick={() => confirmTos(false)}>Cancel</div>
            </div>
          </div>
        </div>
      ) : null}
      {!isAuthenticated && !isLoading && !isLoadingAuth ? (
        <Routes>
          <Route path="/" element={<Splash authState={authState} confirmConnection={confirmConnection} handleError={handleError} />} />
          <Route path="/phone-login" element={<Phone handleError={handleError} handlePhone={handlePhone} />} />
          <Route path="/terms-of-service" element={<Terms />} />
          <Route path="/:slug" element={<Splash authState={authState} confirmConnection={confirmConnection} handleError={handleError} />} />
        </Routes>
      ) : null}
      {!isAuthenticated && !isLoading && !isLoadingAuth && showLogin ? (
        <div id="connection-container" className="popup-overlay" onClick={handleLogin}>
          <div className="popup" onClick={(e) => e.stopPropagation()}>
            <p>How would you like to login?</p>
            <div className="actions">
              <div className="action action-primary" onClick={() => confirmConnection('email')}>By Email</div>
              <div className="action action-primary" onClick={() => confirmConnection('sms')}>By Phone</div>
            </div>
          </div>
        </div>
      ) : null}
      {!isAuthenticated && !isLoading && !isLoadingAuth && showPhone ? (
        <Phone handleError={handleError} handlePhone={handlePhone} />
      ) : null}
      {showFeedback ? (
        <Feedback handleError={handleError} handleFeedback={handleFeedback} />
      ) : null}
      {showStartOver ? (
        <StartOver handleError={handleError} handleStartOver={handleStartOver} />
      ) : null}
      {/* Error UI disabled — errors logged to console instead. To re-enable: uncomment below and call setError(e.message) in handleError */}
      {/*error ? (
        <div id="error-container" className="popup-overlay" onClick={() => confirmError()}>
          <div id="error" className="popup">
            <span>Sorry, please try again</span>
          </div>
        </div>
      ) : null*/}
      {showConfetti ? (
        <Realistic autorun={{ duration: 1, speed: 0.3 }} />
      ) : null}
    </>
  );
}

export default App;
